/* eslint-disable no-unused-vars */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import SidenavCollapse from "pages/Sidenav/SidenavCollapse";
import SidenavList from "pages/Sidenav/SidenavList";
import SidenavItem from "pages/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "pages/Sidenav/SidenavRoot";
// import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  useMaterialUIController,
  setMiniSidenav,
  setSidenavHover,
  setTransparentSidenav,
  setWhiteSidenav,
  setOpenConfigurator,
} from "context";
import { ReactComponent as HamburgerIcon } from "../../assets/images/icons/projectIcons/icon-navigation-menu_24px.svg";
import logo from "../../assets/images/icons/projectIcons/cropped-A.png";

// Material Dashboard 2 PRO React context

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, sidenavHover, transparentSidenav, whiteSidenav, darkMode } = controller;

  // Navigating never changes the menu's open/closed state — only the hamburger does. (No-op kept
  // so the existing onClick wiring stays valid.)
  const collapseAfterNav = () => {};
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  // Step 1: Add a new state variable to track whether "Exit Settings" is clicked

  const navigate = useNavigate();

  const { openConfigurator } = controller;
  const handleConfiguratorOpen = () => {
    setOpenConfigurator(dispatch, !openConfigurator);
    navigate("/home");
  };
  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // ID138 — the hamburger now TOGGLES: pin the sidenav open (from the collapsed rail OR a hover
  // fly-out), or collapse it if already pinned open. Previously it only closed, so the 3 lines
  // appeared to "not work" — you couldn't lock the menu open.
  const toggleSidenav = () => {
    if (miniSidenav || sidenavHover) {
      setMiniSidenav(dispatch, false); // pin open
      setSidenavHover(dispatch, false);
    } else {
      setMiniSidenav(dispatch, true); // collapse
      setSidenavHover(dispatch, false);
    }
  };

  // Auto-expand the section you're in: re-open the current route's group (and nested group) on every
  // navigation, so a sub-page like Horizon Scan is always visible/one-click without manual toggling.
  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, [pathname]);

  // Default to the EXPANDED menu on load. The user opens/closes it only with the hamburger — no
  // width-based auto-collapse and no hover, which was the source of the flaky behavior.
  useEffect(() => {
    setMiniSidenav(dispatch, false);
    setSidenavHover(dispatch, false);
  }, [dispatch]);

  // console.log("miniSidenav", miniSidenav);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href, icon }) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested icon={icon} />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }} onClick={collapseAfterNav}>
          <SidenavItem name={name} active={route === pathname} nested icon={icon} />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, route, href, key, role, icon }) => {
      let returnValue;
      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            icon={icon}
            role={role}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            role={role}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem
              role={role}
              color={color}
              name={name}
              icon={icon}
              active={key === itemName}
            />
          </Link>
        ) : (
          <NavLink
            to={route}
            key={key}
            sx={{ textDecoration: "none" }}
            onClick={(e) => {
              if (key === "home") {
                setOpenConfigurator(dispatch, false);
              }
              collapseAfterNav();
            }}
          >
            <SidenavItem color={color} name={name} icon={icon} active={route === pathname} />
          </NavLink>
        );
      }
      // if (!KcUserService.hasRole(role)) returnValue = "";
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)

  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route, role }) => {
      let returnValue;
      let displayName;
      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink
              to={route}
              key={key}
              onClick={(e) => {
                if (key === "home") {
                  e.preventDefault();
                  setOpenConfigurator(dispatch, false);
                }
                collapseAfterNav();
              }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          /*           if (name === "Profile") {
            displayName = KcUserService.getName();
          } else { */
          displayName = name;
          //          }
          // if (!KcUserService.hasRole(role)) returnValue = "";
          // else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={displayName}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
          // }
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      // Check if the item is "taxonomy" and "Exit Settings" is clicked, then don't render the item.
      if (type === "collapse" && name === "Exit Settings") {
        returnValue = (
          <Link to="/home" key={key} onClick={handleConfiguratorOpen}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox
        pt={3}
        pb={1}
        textAlign="center"
        sx={{
          display: "flex",
          flexDirection: "row",
          // ID138 — when collapsed (no logo), centre the 3 lines so they line up with the closed
          // menu's icons; when expanded, keep the logo + hamburger on opposite ends.
          justifyContent: miniSidenav && !sidenavHover ? "center" : "space-between",
          alignItems: "center",
          mr: miniSidenav && !sidenavHover ? 0 : "13px",
          ml: miniSidenav && !sidenavHover ? 0 : "20px",
        }}
      >
        {/* Hide the logo during the hover fly-out (sidenavHover) so the menu items keep the same
            vertical position as the collapsed rail — otherwise the 66px logo pushes every item
            down and the user clicks the wrong row. Logo shows only when pinned open. */}
        {routes[0].name !== "Exit Settings" && !miniSidenav && !sidenavHover && (
          <img
            alt="logo"
            src={logo}
            style={{
              height: "40px",
              width: "auto",
              maxWidth: "190px",
              objectFit: "contain",
              display: "block",
            }}
          />
        )}
        <MDBox
          display={{ xs: "block", xl: "block" }}
          // position="absolute"
          // top={0}
          // right={0}
          // p={1.625}
          onClick={toggleSidenav}
          sx={{ cursor: "pointer" }}
        >
          {/* <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography> */}
          {routes[0].name !== "Exit Settings" && (
            <HamburgerIcon sx={{ width: "24px", height: "24px" }} />
          )}
        </MDBox>
      </MDBox>
      {/* <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      /> */}
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
